import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import axiosInstance from '../utils/axiosInstance';

const statusContent = {
  accepted: {
    severity: 'success',
    title: 'Invitation accepted',
    message: 'Your invitation was accepted. Temporary credentials will arrive by email.',
    icon: CheckCircleRoundedIcon,
  },
  already_accepted: {
    severity: 'info',
    title: 'Invitation already accepted',
    message: 'This invitation has already been accepted. Use your AMX credentials to sign in.',
    icon: MarkEmailReadRoundedIcon,
  },
  expired: {
    severity: 'warning',
    title: 'Invitation expired',
    message: 'This invitation has expired. Ask your AMX contact to send a new invitation.',
    icon: ErrorOutlineRoundedIcon,
  },
  invalid: {
    severity: 'error',
    title: 'Invitation link invalid',
    message: 'This invitation link is invalid or incomplete. Check the link from your email.',
    icon: ErrorOutlineRoundedIcon,
  },
  error: {
    severity: 'error',
    title: 'Invitation could not be accepted',
    message: 'Something went wrong while accepting this invitation.',
    icon: ErrorOutlineRoundedIcon,
  },
};

function contentForStatus(status) {
  return statusContent[status] || statusContent.error;
}

export default function InviteAcceptPage() {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') || '').trim();
  const didSubmit = useRef(false);
  const [state, setState] = useState({
    loading: true,
    status: token ? 'loading' : 'invalid',
    message: '',
  });

  useEffect(() => {
    if (!token) {
      setState({
        loading: false,
        status: 'invalid',
        message: statusContent.invalid.message,
      });
      return;
    }

    if (didSubmit.current) {
      return;
    }
    didSubmit.current = true;

    async function acceptInvite() {
      try {
        const response = await axiosInstance.get('/partners/invites/accept', {
          params: { token },
          headers: { Accept: 'application/json' },
        });
        setState({
          loading: false,
          status: response.data?.invite_status || 'accepted',
          message: response.data?.message || statusContent.accepted.message,
        });
      } catch (error) {
        const data = error?.response?.data || {};
        setState({
          loading: false,
          status: data.invite_status || 'error',
          message: data.message || statusContent.error.message,
        });
      }
    }

    acceptInvite();
  }, [token]);

  const display = contentForStatus(state.status);
  const Icon = display.icon;
  const severity = display.severity;
  const message = state.message || display.message;
  const iconColor = severity === 'success'
    ? 'success'
    : severity === 'warning'
      ? 'warning'
      : severity === 'info'
        ? 'info'
        : 'error';

  return (
    <>
      <CssBaseline enableColorScheme />
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#eef3f8',
          color: '#16283d',
          display: 'grid',
          placeItems: 'center',
          px: 2,
          py: 5,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: 'min(100%, 620px)',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: { xs: 3, md: 4 },
          }}
        >
          <Stack spacing={3} alignItems="stretch">
            <Stack spacing={1.5} alignItems="center" textAlign="center">
              <Box
                component="img"
                src="/images/AMX-Logo.png"
                alt="AMX"
                sx={{ width: 'min(100%, 280px)', height: 72, objectFit: 'contain' }}
              />
              {state.loading ? (
                <CircularProgress size={36} />
              ) : (
                <Icon color={iconColor} sx={{ fontSize: 48 }} />
              )}
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                {state.loading ? 'Accepting invitation' : display.title}
              </Typography>
            </Stack>

            {state.loading ? (
              <Typography variant="body1" color="text.secondary" textAlign="center">
                Please wait while AMX accepts your invitation.
              </Typography>
            ) : (
              <Alert severity={severity}>
                {message}
              </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                disabled={state.loading || state.status === 'expired' || state.status === 'invalid'}
              >
                Sign In
              </Button>
              <Button component={RouterLink} to="/welcome" variant="outlined">
                AMX Home
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    </>
  );
}
